import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { cn } from '../lib/utils';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';

const faqs = [
  {
    q: 'What is a credit?',
    a: 'One credit = one page processed. Raw OCR (/api/v1/ocr) costs 1 credit per page. Field extraction (/api/v1/ocr/extract) costs 3 credits per page. Credits are deducted only on successful processing.',
  },
  {
    q: 'What is dynamic field extraction?',
    a: 'Instead of returning all text, you specify which field names you want — e.g. "vendor_name, total_amount, due_date". The API finds those fields in the document and returns a structured JSON with value + confidence score for each one.',
  },
  {
    q: 'Is my data private?',
    a: 'Yes. All processing runs on our private server — no cloud AI, no third-party APIs. Documents are processed entirely in-memory. Nothing is written to disk and nothing is sent to external services.',
  },
  {
    q: 'What file formats do you support?',
    a: 'PDF (up to 10 pages), PNG, JPEG, WEBP, and TIFF — up to 20 MB per file. Multi-page PDFs are processed page by page automatically.',
  },
  {
    q: 'How long does processing take?',
    a: 'Raw OCR takes about 50ms per page. Field extraction (which uses a local AI model for structured output) takes around 30–60 seconds per page depending on the number of fields requested.',
  },
  {
    q: 'How do credits expire?',
    a: 'Credits on monthly plans reset each billing cycle. The 10 free credits from sign-up do not expire — you can use them any time to test the API.',
  },
  {
    q: 'How does the API work?',
    a: 'Send a multipart POST request to /api/v1/ocr/extract with your file and a "fields" form field (comma-separated field names). Include your API key as a Bearer token in the Authorization header. You get a JSON response back.',
  },
  {
    q: 'How do I pay for a higher plan?',
    a: 'Paid plans are coming soon. For now, contact us at support@tyosolution.com to arrange a manual top-up or discuss Enterprise pricing.',
  },
];

export default function FAQSection() {
  const [ref, visible] = useScrollAnimation();

  return (
    <section ref={ref} className={cn('py-24 bg-background reveal', visible && 'revealed')}>
      <div className='max-w-3xl mx-auto px-4 sm:px-6 lg:px-8'>
        {/* Header */}
        <div className='text-center mb-14'>
          <span className='inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-accent/10 text-accent-dark border border-accent/20 mb-4'>
            FAQ
          </span>
          <h2 className='font-heading font-bold text-4xl lg:text-5xl text-foreground mb-4'>
            Frequently asked questions
          </h2>
          <p className='text-muted-foreground text-lg'>Got more questions? Email us at support@tyosolution.com</p>
        </div>

        <Accordion type='single' collapsible className='space-y-3'>
          {faqs.map((item, i) => (
            <AccordionItem
              key={i}
              value={`item-${i}`}
              className={cn(
                'bg-card border border-border rounded-xl px-6 data-[state=open]:shadow-card transition-shadow duration-200 reveal',
                `reveal-delay-${(i % 4) + 1}`,
                visible && 'revealed'
              )}
            >
              <AccordionTrigger className='text-left font-heading font-medium text-foreground py-5 hover:no-underline text-sm leading-snug'>
                {item.q}
              </AccordionTrigger>
              <AccordionContent className='text-muted-foreground text-sm leading-relaxed pb-5'>
                {item.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}
